import { useCallback, useState } from "react";
import {
  InstagramWebviewEscapeOverlay,
  type IgEscapeAction,
} from "@/components/InstagramWebviewEscapeOverlay";
import {
  isMercadoPagoHostedUrl,
  navigateToMercadoPagoCheckout,
  openMercadoPagoInExternalBrowser,
  shouldUseMercadoPagoInstagramEscape,
} from "@/lib/mercadopagoCheckoutRedirect";

export type MercadoPagoInstagramEscapeOptions = {
  language?: "es" | "en" | "pt";
  /** Analytics surface id, e.g. hero_cta_mejores_amigos */
  surface?: string;
  countryCode?: string;
};

function captureIgMpEscape(
  event: string,
  props: Record<string, string | boolean | undefined>,
): void {
  const w = window as unknown as {
    posthogSafeCapture?: (e: string, p?: Record<string, unknown>) => void;
  };
  if (typeof w.posthogSafeCapture === "function") {
    w.posthogSafeCapture(event, props);
  }
}

/**
 * Instagram / Meta IAB cannot complete MercadoPago checkout in-app (CSP).
 * Intercept MP CTA clicks and show the same escape overlay used for Stripe VIP.
 */
export function useMercadoPagoInstagramEscape(
  opts: MercadoPagoInstagramEscapeOptions = {},
) {
  const [escape, setEscape] = useState<{ open: boolean; url: string }>({
    open: false,
    url: "",
  });

  const interceptMercadoPagoCta = useCallback(
    (url: string): boolean => {
      if (!isMercadoPagoHostedUrl(url) || !shouldUseMercadoPagoInstagramEscape()) {
        return false;
      }
      captureIgMpEscape("ig_webview_escape_shown", {
        surface: opts.surface ?? "mercadopago_cta",
        country: opts.countryCode,
        destination_host: "mercadopago",
      });
      setEscape({ open: true, url });
      return true;
    },
    [opts.surface, opts.countryCode],
  );

  const close = useCallback(() => setEscape({ open: false, url: "" }), []);

  const overlay = (
    <InstagramWebviewEscapeOverlay
      open={escape.open}
      destinationUrl={escape.url}
      language={opts.language ?? "es"}
      onClose={close}
      onOpenInBrowser={() => openMercadoPagoInExternalBrowser(escape.url)}
      onContinueAnyway={() => {
        const url = escape.url;
        close();
        navigateToMercadoPagoCheckout(url);
      }}
      onAction={(action: IgEscapeAction) => {
        captureIgMpEscape("ig_webview_escape_action", {
          action,
          surface: opts.surface ?? "mercadopago_cta",
          country: opts.countryCode,
        });
      }}
    />
  );

  return { interceptMercadoPagoCta, overlay };
}